import React, { useState, useEffect } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Spinner,
    Alert,
    Badge,
    Button,
    Input,
    Label,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    UncontrolledTooltip,
    Pagination,        // Eklendi
    PaginationItem,    // Eklendi
    PaginationLink     // Eklendi
} from "reactstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../../api/urls";
import { updatePaymentAccounted } from "../../api/requests/payment-requests";
import { fetchAuthMe } from "../../api/requests/auth-requests";
import DateInput from "../../components/Common/DateInput";

// Toast gösterme fonksiyonu
const showToast = (type, msg) => {
    const map = { danger: "error", error: "error" };
    const fn = toast[map[type] || type] || toast.info;
    fn(msg, { autoClose: 3000 });
};

// cardClassName: bir sekme gövdesi içinde gösterildiğinde iç içe kart görünümünü
// engellemek için dışarıdan sadeleştirilebilir (varsayılan: bağımsız kart).
const AccountMovementList = ({
    accountId,
    formatCurrency,
    onMovementDeleted,
    cardClassName = "bg-white border-0 shadow-lg rounded-4",
}) => {
    const [movements, setMovements] = useState([]);
    const [loadingMovements, setLoadingMovements] = useState(true);
    const [movementsError, setMovementsError] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [modal, setModal] = useState(false);
    const [movementToDelete, setMovementToDelete] = useState(null);
    const [downloadingReconciliation, setDownloadingReconciliation] = useState(false);
    const [emailingReconciliation, setEmailingReconciliation] = useState(false);
    // Mahsuplaşma dönem (manuel tarih aralığı) — boş ise backend varsayılanı (tüm geçmiş → bugün)
    const [reconStartDate, setReconStartDate] = useState("");
    const [reconEndDate, setReconEndDate] = useState("");
    // Carinin kayıtlı yetkili e-postası yoksa backend 422 döner; adres bu modalda sorulur.
    const [recipientModal, setRecipientModal] = useState(false);
    const [recipientEmail, setRecipientEmail] = useState("");
    const [recipientError, setRecipientError] = useState("");
    const [currentUser, setCurrentUser] = useState(null);
    const [accountingLoading, setAccountingLoading] = useState({});

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    // --- End Pagination State ---

    const MOVEMENT_LIMIT = 10;
    const toggleModal = () => setModal(!modal);

    // --- Stil Renkleri ---
    const primaryColor = '#0A316E'; // Lacivert
    const secondaryColor = '#FFA500'; // Turuncu

    const getPageStyle = (active = false) => ({
        backgroundColor: active ? primaryColor : 'transparent',
        borderColor: active ? primaryColor : secondaryColor,
        color: active ? 'white' : secondaryColor,
        boxShadow: 'none',
        margin: '0 2px' // Butonlar arası boşluk
    });

    const getDisabledStyle = () => ({
        backgroundColor: 'transparent',
        borderColor: '#ccc',
        color: '#ccc',
        opacity: 0.7,
        cursor: 'not-allowed',
        margin: '0 2px'
    });
    // --- End Stil ---

    useEffect(() => {
        if (accountId) {
            // accountId veya mahsuplaşma dönem tarihleri değiştiğinde ilk sayfayı getir
            fetchRecentMovements(accountId, 0);
        }
    }, [accountId, reconStartDate, reconEndDate]);

    useEffect(() => {
        const loadUser = async () => {
            try {
                const res = await fetchAuthMe();
                if (res.ok) {
                    const { data } = await res.json();
                    setCurrentUser(data);
                }
            } catch { /* sessizce geç */ }
        };
        loadUser();
    }, []);

    const canToggleAccounted = () => {
        const roles = currentUser?.roles || [];
        return roles.some(r => ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT"].includes(r));
    };

    const handleToggleAccounted = async (item) => {
        if (!item.paymentId) return;
        const newValue = !item.accounted;
        setAccountingLoading(prev => ({ ...prev, [item.id]: true }));
        try {
            await updatePaymentAccounted(item.paymentId, newValue);
            setMovements(prev => prev.map(m => m.id === item.id ? { ...m, accounted: newValue } : m));
            showToast("success", newValue ? "Muhasebeleşti olarak işaretlendi" : "Muhasebeleşmedi olarak işaretlendi");
        } catch {
            showToast("error", "Muhasebe durumu güncellenemedi");
        } finally {
            setAccountingLoading(prev => ({ ...prev, [item.id]: false }));
        }
    };

    const fetchRecentMovements = async (id, page = 0) => {
        setLoadingMovements(true);
        setMovementsError(null);
        try {
            // API çağrısını 'page' + mahsuplaşma dönem (tarih) filtresi ile güncelle
            const res = await axios.get(API.account_movements(id, page, MOVEMENT_LIMIT, {
                startDate: reconStartDate || undefined,
                endDate: reconEndDate || undefined,
            }));

            const pageInfo = res.data.data;
            const data = pageInfo.content || [];
            setMovements(data);
            setTotalPages(pageInfo.totalPages || 0);
            setCurrentPage(pageInfo.number || 0);

        } catch (err) {
            console.error("Cari hareketleri yüklenirken hata:", err);
            setMovementsError(err.response?.data?.message || "Cari hareketleri yüklenirken bir hata oluştu.");
            setMovements([]);
            setTotalPages(0); // Hata durumunda sıfırla
            setCurrentPage(0); // Hata durumunda sıfırla
        } finally {
            setLoadingMovements(false);
        }
    };

    const handleDeleteClick = (movement) => {
        setMovementToDelete(movement);
        toggleModal();
    };

    const confirmDelete = async () => {
        if (!movementToDelete) return;

        setIsDeleting(true);
        const movementId = movementToDelete.id;
        // ... (Diğer değişkenler toast için)
        const movementAmount = movementToDelete.amount;
        const movementCurrency = movementToDelete.currency;
        const movementType = movementToDelete.mainMovementType === 'DEBT' ? 'Borç' : 'Tahsilat';

        try {
            await axios.delete(API.remove_movement(movementId));

            // Başarılı toast
            showToast(
                "success",
                `${movementType} hareketi başarıyla silindi! Tutar: ${movementAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ${movementCurrency}`
            );

            // Modal'ı kapat
            toggleModal();
            setMovementToDelete(null);

            let pageToFetch = currentPage;
            if (movements.length === 1 && currentPage > 0) {
                pageToFetch = currentPage - 1;
            }
            fetchRecentMovements(accountId, pageToFetch);

            // Toast mesajının görünmesi için 3.5 saniye bekle, sonra parent'ı güncelle
            setTimeout(() => {
                if (onMovementDeleted) {
                    onMovementDeleted();
                }
            }, 3500);

        } catch (err) {
            console.error("Hareket silinirken hata:", err);
            // ... (Hata toast)
            showToast(
                "error",
                err.response?.data?.message || "Hareket silinirken bir hata oluştu. Lütfen tekrar deneyiniz."
            );
            toggleModal();
            setMovementToDelete(null);
        } finally {
            setIsDeleting(false);
        }
    };

    // --- Sayfa Değişim Handler ---
    const handlePageChange = (page) => {
        if (page < 0 || page >= totalPages || page === currentPage) {
            return; // Geçersiz sayfa veya mevcut sayfa ise işlem yapma
        }
        fetchRecentMovements(accountId, page);
    };

    // --- Sayfa Numaralarını Render Etmek İçin Yardımcı Fonksiyon ---
    const renderPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 3; // Ortada gösterilecek maksimum sayfa sayısı
        let startPage, endPage;

        if (totalPages <= maxPagesToShow + 2) { // 5 veya daha az sayfa varsa
            startPage = 0;
            endPage = totalPages;
        } else {
            if (currentPage <= Math.floor(maxPagesToShow / 2) + 1) { // Başlangıca yakınsa
                startPage = 0;
                endPage = maxPagesToShow;
            } else if (currentPage + Math.floor(maxPagesToShow / 2) >= totalPages - 1) { // Sona yakınsa
                startPage = totalPages - maxPagesToShow;
                endPage = totalPages;
            } else { // Ortada ise
                startPage = currentPage - Math.floor(maxPagesToShow / 2);
                endPage = currentPage + Math.ceil(maxPagesToShow / 2);
            }
        }

        // ... (ellipsis) başlangıç için
        if (startPage > 0) {
            pages.push(
                <PaginationItem key="start-ellipsis" disabled>
                    <PaginationLink style={getDisabledStyle()}>...</PaginationLink>
                </PaginationItem>
            );
        }

        // Sayfa numaraları
        for (let i = startPage; i < endPage; i++) {
            pages.push(
                <PaginationItem active={i === currentPage} key={i}>
                    <PaginationLink
                        onClick={() => handlePageChange(i)}
                        style={getPageStyle(i === currentPage)}
                    >
                        {i + 1}
                    </PaginationLink>
                </PaginationItem>
            );
        }

        // ... (ellipsis) bitiş için
        if (endPage < totalPages) {
            pages.push(
                <PaginationItem key="end-ellipsis" disabled>
                    <PaginationLink style={getDisabledStyle()}>...</PaginationLink>
                </PaginationItem>
            );
        }

        return pages;
    };

    // --- Mahsuplaşma ---
    // Rapor seçili dönemdeki hareketlerden üretilir; hareket yoksa backend boş PDF döner.
    const hasMovements = movements.length > 0;

    const handleDownloadReconciliationPdf = async () => {
        if (!hasMovements) {
            showToast("error", "Seçili dönemde cari hareketi yok, mahsuplaşma raporu oluşturulamaz.");
            return;
        }
        setDownloadingReconciliation(true);
        try {
            const payload = { accountId };
            if (reconStartDate) payload.startDate = reconStartDate;
            if (reconEndDate) payload.endDate = reconEndDate;
            const res = await axios.post(API.account_reconciliation_pdf_download, payload, {
                responseType: "blob",
            });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const cd = res.headers["content-disposition"] || "";
            const match = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^"]+)"?/i);
            link.download = match?.[1] ? decodeURIComponent(match[1]) : `mahsuplasma-${accountId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            showToast("success", "Mahsuplaşma PDF dosyası indirildi.");
        } catch (err) {
            showToast("error", err?.response?.data?.message || "Mahsuplaşma PDF indirilemedi.");
        } finally {
            setDownloadingReconciliation(false);
        }
    };

    /**
     * Mahsuplaşma PDF'ini e-posta ile gönderir.
     * @param {string} [overrideEmail] Elle girilen alıcı adresi. Verilmezse backend
     *   carinin yetkili kullanıcısının adresini kullanır; bulamazsa 422 döner ve
     *   adresi sormak için modal açılır.
     */
    const handleEmailReconciliationPdf = async (overrideEmail) => {
        if (!hasMovements) {
            showToast("error", "Seçili dönemde cari hareketi yok, mahsuplaşma raporu gönderilemez.");
            return;
        }
        setEmailingReconciliation(true);
        try {
            const payload = { accountId };
            if (reconStartDate) payload.startDate = reconStartDate;
            if (reconEndDate) payload.endDate = reconEndDate;
            if (overrideEmail) payload.recipientEmail = overrideEmail;
            const res = await axios.post(API.account_reconciliation_pdf_email, payload);
            setRecipientModal(false);
            setRecipientEmail("");
            setRecipientError("");
            showToast("success", overrideEmail
                ? `Mahsuplaşma raporu ${overrideEmail} adresine gönderildi.`
                : res?.data?.message || "Mahsuplaşma PDF e-posta olarak gönderildi.");
        } catch (err) {
            const status = err?.response?.data?.statusCode ?? err?.response?.status;
            const message = err?.response?.data?.message || "Mahsuplaşma PDF e-posta gönderimi başarısız.";
            // 422 = cari bulundu, rapor hazır, yalnız alıcı adresi yok → adresi sor.
            if (status === 422) {
                setRecipientError(overrideEmail ? message : "");
                setRecipientModal(true);
            } else {
                showToast("error", message);
            }
        } finally {
            setEmailingReconciliation(false);
        }
    };

    const submitRecipientEmail = () => {
        const email = recipientEmail.trim();
        // Backend @Email ile zaten doğruluyor; buradaki kontrol gereksiz bir istek atmamak için.
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setRecipientError("Geçerli bir e-posta adresi girin.");
            return;
        }
        setRecipientError("");
        handleEmailReconciliationPdf(email);
    };
    // --- End Mahsuplaşma ---

    // --- Helper Fonksiyonlar (getAmountColor, getMovementTypeText vb.) ---
    // (Bu fonksiyonlar değişmediği için aynı kalıyor)
    const getAmountColor = (type) => (type === 'DEBT' ? 'danger' : 'success');
    const getMovementTypeText = (item) => {
        return item.mainMovementType === 'DEBT' ? 'Borç' : 'Tahsilat';
    };
    const getReceiptTypeText = (receiptType) => {
        if (!receiptType) return '-';
        const receiptTypeMap = {
            'CASH': 'Nakit', 'CREDIT_CARD': 'Kredi Kartı', 'BANK_TRANSFER': 'Havale/EFT',
            'CHECK': 'Çek', 'PROMISSORY_NOTE': 'Senet'
        };
        return receiptTypeMap[receiptType] || receiptType;
    };
    const getInvoiceStatusText = (status) => {
        if (!status) return '-';
        const statusMap = { 'OPEN': 'Açık', 'CLOSED': 'Kapalı', 'CANCELED': 'İptal', 'PARTIALLY_PAID': 'Kısmen Ödendi' };
        return statusMap[status] || status;
    };
    const truncateText = (text, maxLength = 30) => {
        if (!text) return '-';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };
    const getCheckOrPromissoryNoteNo = (item) => {
        if (item.checkNumber) return { type: 'Çek No', value: item.checkNumber };
        if (item.promissoryNoteNumber) return { type: 'Senet No', value: item.promissoryNoteNumber };
        return { type: null, value: null };
    };
    // --- End Helper Fonksiyonlar ---

    return (
        <React.Fragment>
            <Card className={cardClassName}>
                <CardHeader className="bg-white border-0 pb-0 rounded-top-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <h5 className="mb-0 text-dark fw-semibold">
                            <i className="bx bx-list-ul me-2"></i>
                            Son Cari Hesap Hareketleri
                        </h5>
                        <div className="d-flex flex-wrap align-items-end gap-2">
                            <div>
                                <Label className="text-muted mb-0 d-block" style={{ fontSize: ".68rem" }}>Başlangıç</Label>
                                <DateInput
                                    className="form-control-sm"
                                    value={reconStartDate}
                                    maxDate={reconEndDate}
                                    onChange={setReconStartDate}
                                    style={{ width: 150 }}
                                    title="Mahsuplaşma başlangıç tarihi (boş = tüm geçmiş)"
                                />
                            </div>
                            <div>
                                <Label className="text-muted mb-0 d-block" style={{ fontSize: ".68rem" }}>Bitiş</Label>
                                <DateInput
                                    className="form-control-sm"
                                    value={reconEndDate}
                                    minDate={reconStartDate}
                                    onChange={setReconEndDate}
                                    style={{ width: 150 }}
                                    title="Mahsuplaşma bitiş tarihi (boş = bugün)"
                                />
                            </div>
                            <Button
                                color="light"
                                size="sm"
                                className="rounded-pill border"
                                onClick={handleDownloadReconciliationPdf}
                                disabled={downloadingReconciliation || emailingReconciliation || loadingMovements || !hasMovements}
                                title={!loadingMovements && !hasMovements ? "Seçili dönemde cari hareketi yok" : undefined}
                            >
                                {downloadingReconciliation
                                    ? <><Spinner size="sm" className="me-1" />İndiriliyor...</>
                                    : <><i className="bx bx-download me-1"></i>Mahsuplaşma İndir</>}
                            </Button>
                            {/* onClick sarmalayıcı şart: fonksiyon doğrudan bağlanırsa
                                tıklama olayı overrideEmail argümanı olarak geçer. */}
                            <Button
                                color="primary"
                                size="sm"
                                className="rounded-pill"
                                onClick={() => handleEmailReconciliationPdf()}
                                disabled={emailingReconciliation || downloadingReconciliation || loadingMovements || !hasMovements}
                                title={!loadingMovements && !hasMovements ? "Seçili dönemde cari hareketi yok" : undefined}
                            >
                                {emailingReconciliation
                                    ? <><Spinner size="sm" className="me-1" />Gönderiliyor...</>
                                    : <><i className="bx bx-envelope me-1"></i>Mahsuplaşma Gönder</>}
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardBody className="p-0 pt-3 rounded-bottom-4">
                    {loadingMovements ? (
                        <div className="text-center p-5">
                            <Spinner size="sm" className="me-2" />
                            <span className="text-muted small">Hareketler yükleniyor...</span>
                        </div>
                    ) : movementsError ? (
                        <Alert color="danger" className="m-4 border-0 rounded-4">
                            <i className="bx bx-error me-1"></i> {movementsError}
                        </Alert>
                    ) : movements.length > 0 ? (
                        <>
                            <div className="table-responsive"> {/* rounded-bottom-4 buradan kaldırıldı */}
                                <table className="table align-middle mb-0" style={{ fontSize: '0.85rem' }}>
                                    <thead style={{ backgroundColor: '#f9f9f9' }}>
                                    <tr>
                                        {/* th (başlıklar) aynı kaldı */}
                                        <th className="border-0 text-muted fw-medium ps-4">Tip</th>
                                        <th className="border-0 text-muted fw-medium">Alt Tip</th>
                                        <th className="border-0 text-muted fw-medium">Tutar</th>
                                        <th className="border-0 text-muted fw-medium">Tarih</th>
                                        <th className="border-0 text-muted fw-medium">Vade</th>
                                        <th className="border-0 text-muted fw-medium">Belge No</th>
                                        <th className="border-0 text-muted fw-medium">Banka</th>
                                        <th className="border-0 text-muted fw-medium">Çek/Senet No</th>
                                        <th className="border-0 text-muted fw-medium">Fatura Durumu</th>
                                        <th className="border-0 text-muted fw-medium">Açıklama</th>
                                        {canToggleAccounted() && <th className="border-0 text-muted fw-medium text-center">Muhasebeleşildi</th>}
                                        <th className="border-0 text-muted fw-medium text-center">İşlem</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {/* (movements.map kısmı aynı kaldı) */}
                                    {movements.map((item) => {
                                        const checkNote = getCheckOrPromissoryNoteNo(item);
                                        return (
                                            <tr key={item.id} className="border-bottom">
                                                <td className="ps-4 py-3">
                                                    <Badge
                                                        color={item.mainMovementType === 'DEBT' ? 'danger' : 'success'}
                                                        className="rounded-pill px-2 py-1"
                                                        style={{ fontSize: '0.7rem' }}
                                                    >
                                                        {getMovementTypeText(item)}
                                                    </Badge>
                                                </td>
                                                <td className="py-3">
                                                    <span className="small text-muted">
                                                        {item.mainMovementType === 'DEBT'
                                                            ? '-'
                                                            : getReceiptTypeText(item.receiptType)}
                                                    </span>
                                                </td>
                                                <td className="py-3">
                                                    <span className={`fw-bold text-${getAmountColor(item.mainMovementType)}`}>
                                                        {formatCurrency(item.amount)}
                                                    </span>
                                                    {item.currency && <span className="text-muted ms-1 small">({item.currency})</span>}
                                                </td>
                                                <td className="py-3">
                                                    <span className="small">
                                                        {new Date(item.movementDate).toLocaleDateString('tr-TR')}
                                                    </span>
                                                </td>
                                                <td className="py-3">
                                                    <span className="small text-muted">
                                                        {item.dueDate
                                                            ? new Date(item.dueDate).toLocaleDateString('tr-TR')
                                                            : '-'}
                                                    </span>
                                                </td>
                                                <td className="py-3">
                                                    {item.documentNumber ? (
                                                        <code className="text-dark small bg-light px-2 py-1 rounded">
                                                            {item.documentNumber}
                                                        </code>
                                                    ) : (
                                                        <span className="small text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="py-3">
                                                    <span className="small text-muted">
                                                        {item.bankName || '-'}
                                                    </span>
                                                </td>
                                                <td className="py-3">
                                                    {checkNote.value ? (
                                                        <code className="text-info small bg-light px-2 py-1 rounded">
                                                            {checkNote.value}
                                                        </code>
                                                    ) : (
                                                        <span className="small text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="py-3">
                                                    <span className="small text-muted">
                                                        {item.mainMovementType === 'DEBT'
                                                            ? getInvoiceStatusText(item.invoiceStatus)
                                                            : '-'}
                                                    </span>
                                                </td>
                                                <td className="py-3">
                                                    {item.generalDescription && item.generalDescription.length > 30 ? (
                                                        <>
                                                            <span id={`desc-${item.id}`} className="small text-muted" style={{ cursor: 'pointer' }}>
                                                                {truncateText(item.generalDescription)}
                                                            </span>
                                                            <UncontrolledTooltip placement="top" target={`desc-${item.id}`}>
                                                                {item.generalDescription}
                                                            </UncontrolledTooltip>
                                                        </>
                                                    ) : (
                                                        <span className="small text-muted">
                                                            {item.generalDescription || '-'}
                                                        </span>
                                                    )}
                                                </td>
                                                {canToggleAccounted() && (
                                                    <td className="text-center py-3">
                                                        {item.paymentId ? (
                                                            <button
                                                                className="btn btn-sm rounded-pill px-2 py-1 d-inline-flex align-items-center gap-1"
                                                                style={{
                                                                    fontSize: "0.75rem",
                                                                    minWidth: 80,
                                                                    cursor: accountingLoading[item.id] ? "wait" : "pointer",
                                                                    backgroundColor: item.accounted ? "rgba(113,195,11,.12)" : "rgba(108,117,125,.1)",
                                                                    color: item.accounted ? "#71C30B" : "#6c757d",
                                                                    border: `1px solid ${item.accounted ? "#71C30B30" : "#6c757d20"}`,
                                                                    transition: "all .2s",
                                                                }}
                                                                disabled={accountingLoading[item.id]}
                                                                onClick={() => handleToggleAccounted(item)}
                                                                title={item.accounted ? "Muhasebeleşmedi olarak işaretle" : "Muhasebeleşti olarak işaretle"}
                                                                onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.06)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,.12)"; }}
                                                                onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "none"; }}
                                                            >
                                                                {accountingLoading[item.id] ? (
                                                                    <Spinner size="sm" style={{ width: 12, height: 12 }} />
                                                                ) : (
                                                                    <>
                                                                        <i className={`bx ${item.accounted ? "bx-check-circle" : "bx-circle"} me-1`} />
                                                                        {item.accounted ? "Evet" : "Hayır"}
                                                                    </>
                                                                )}
                                                            </button>
                                                        ) : (
                                                            <span className="small text-muted">-</span>
                                                        )}
                                                    </td>
                                                )}
                                                <td className="text-center py-3">
                                                    {(item.receiptType === 'CREDIT_CARD' || item.paymentId) ? (
                                                        <Button color="secondary" size="sm" className="rounded-circle" style={{ width: '32px', height: '32px', padding: '0', opacity: 0.5, cursor: 'not-allowed' }} title="Kredi Kartı Tahsilatları Silinemez" disabled>
                                                            <i className="bx bx-lock"></i>
                                                        </Button>
                                                    ) : (
                                                        <Button color="danger" size="sm" onClick={() => handleDeleteClick(item)} className="rounded-circle" style={{ width: '32px', height: '32px', padding: '0' }} title="Hareketi Sil">
                                                            <i className="bx bx-trash"></i>
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>

                            {/* --- YENİ EKLENEN PAGINATION BÖLÜMÜ --- */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-end align-items-center p-3 rounded-bottom-4" style={{ backgroundColor: '#f9f9f9' }}>
                                    <span className="me-3 small text-muted">
                                        Sayfa {currentPage + 1} / {totalPages}
                                    </span>
                                    <Pagination aria-label="Page navigation" size="sm" className="mb-0">
                                        <PaginationItem disabled={currentPage <= 0}>
                                            <PaginationLink
                                                first
                                                onClick={() => handlePageChange(0)}
                                                style={currentPage <= 0 ? getDisabledStyle() : getPageStyle()}
                                            >
                                                <i className="bx bx-chevrons-left"></i>
                                            </PaginationLink>
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPage <= 0}>
                                            <PaginationLink
                                                previous
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                style={currentPage <= 0 ? getDisabledStyle() : getPageStyle()}
                                            >
                                                <i className="bx bx-chevron-left"></i>
                                            </PaginationLink>
                                        </PaginationItem>

                                        {renderPageNumbers()}

                                        <PaginationItem disabled={currentPage >= totalPages - 1}>
                                            <PaginationLink
                                                next
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                style={currentPage >= totalPages - 1 ? getDisabledStyle() : getPageStyle()}
                                            >
                                                <i className="bx bx-chevron-right"></i>
                                            </PaginationLink>
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPage >= totalPages - 1}>
                                            <PaginationLink
                                                last
                                                onClick={() => handlePageChange(totalPages - 1)}
                                                style={currentPage >= totalPages - 1 ? getDisabledStyle() : getPageStyle()}
                                            >
                                                <i className="bx bx-chevrons-right"></i>
                                            </PaginationLink>
                                        </PaginationItem>
                                    </Pagination>
                                </div>
                            )}
                            {/* --- END PAGINATION BÖLÜMÜ --- */}
                        </>
                    ) : (
                        <Alert color="info" className="m-4 border-0 rounded-4">
                            <i className="bx bx-info-circle me-1"></i>
                            Cariye ait hareket kaydı bulunmamaktadır.
                        </Alert>
                    )}
                </CardBody>
            </Card>

            {/* Modal ve ToastContainer kodları aynı kalır */}
            <Modal isOpen={modal} toggle={toggleModal} centered>
                <ModalHeader toggle={toggleModal} className="border-0 pb-0">
                    <h5 className="mb-0 fw-semibold">Hareket Silme Onayı</h5>
                </ModalHeader>
                <ModalBody className="pt-3">
                    {movementToDelete && (
                        <React.Fragment>
                            <Alert color="warning" className="border-0 rounded-4 mb-3">
                                <i className="bx bx-warning me-2"></i>
                                <strong>Dikkat!</strong> Bu işlem geri alınamaz. Silme işlemi cari bakiyeyi tersine çevirecektir.
                            </Alert>
                            <div className="border rounded-4 p-3" style={{ backgroundColor: '#f9f9f9' }}>
                                <div className="mb-2">
                                    <small className="text-muted d-block">Hareket Tipi</small>
                                    <span className="fw-medium">
                                        {movementToDelete.mainMovementType === 'DEBT' ? 'Borç' : 'Tahsilat'}
                                    </span>
                                </div>
                                <div>
                                    <small className="text-muted d-block">Tutar</small>
                                    <span className={`fw-bold fs-5 text-${getAmountColor(movementToDelete.mainMovementType)}`}>
                                        {formatCurrency(movementToDelete.amount)} {movementToDelete.currency}
                                    </span>
                                </div>
                            </div>
                            <p className="mt-3 mb-0 text-center small text-muted">
                                Bu hareketi silmek istediğinizden emin misiniz?
                            </p>
                        </React.Fragment>
                    )}
                </ModalBody>
                <ModalFooter className="border-0">
                    <Button
                        color="secondary"
                        onClick={toggleModal}
                        disabled={isDeleting}
                        className="rounded-pill px-4"
                    >
                        Vazgeç
                    </Button>
                    <Button
                        color="danger"
                        onClick={confirmDelete}
                        disabled={isDeleting}
                        className="rounded-pill px-4"
                    >
                        {isDeleting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Siliniyor...
                            </>
                        ) : (
                            'Evet, Sil'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Alıcı e-postası sorma: carinin kayıtlı yetkili adresi yoksa backend 422
                döner ve gönderim bu modaldan girilen adresle tekrarlanır. Adres yalnız
                bu istek için kullanılır, cariye kaydedilmez. */}
            <Modal
                isOpen={recipientModal}
                toggle={() => setRecipientModal(!recipientModal)}
                centered
            >
                <ModalHeader toggle={() => setRecipientModal(false)} className="border-0 pb-0">
                    E-posta Adresi Gerekli
                </ModalHeader>
                <ModalBody className="pt-3">
                    <p className="text-muted mb-3">
                        Bu cari için kayıtlı bir yetkili e-posta adresi bulunamadı.
                        Mahsuplaşma raporunun gönderileceği adresi girin.
                    </p>
                    <Label for="reconRecipientEmail" className="form-label">
                        E-posta Adresi
                    </Label>
                    <Input
                        id="reconRecipientEmail"
                        type="email"
                        placeholder="ornek@firma.com"
                        value={recipientEmail}
                        autoFocus
                        invalid={!!recipientError}
                        onChange={(e) => {
                            setRecipientEmail(e.target.value);
                            if (recipientError) setRecipientError("");
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !emailingReconciliation) submitRecipientEmail();
                        }}
                    />
                    {recipientError && (
                        <div className="invalid-feedback d-block">{recipientError}</div>
                    )}
                    <small className="text-muted d-block mt-2">
                        Girilen adres yalnızca bu gönderim için kullanılır, cari kaydına eklenmez.
                    </small>
                </ModalBody>
                <ModalFooter className="border-0">
                    <Button
                        color="secondary"
                        className="rounded-pill px-4"
                        onClick={() => setRecipientModal(false)}
                        disabled={emailingReconciliation}
                    >
                        Vazgeç
                    </Button>
                    <Button
                        color="primary"
                        className="rounded-pill px-4"
                        onClick={submitRecipientEmail}
                        disabled={emailingReconciliation}
                    >
                        {emailingReconciliation ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Gönderiliyor...
                            </>
                        ) : (
                            'Gönder'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

        </React.Fragment>
    );
};

export default AccountMovementList;